import { convertBytesToString } from "../../components/FileSize";
import { ExpandedLocalDetails } from "../samples/@types/expanded_local_details";
import { FileSimilaritySample } from "./@types";
import { EMPTY_VALUE } from "./consts";
import { diffSets, enabledCapabilities } from "./utils";

export type MatrixColumnName =
  | "file_type"
  | "file_size"
  | "extracted_files"
  | "threat_name"
  | "av_detections"
  | "cert_validation"
  | "system_tags"
  | "capabilities";

/**
 * Raw, never formatted: two sizes that both render as "2.2 MB" are not the same
 * file size. `undefined` is "no data" and claims no difference; "" is "loaded
 * and genuinely absent", which does differ from a value.
 */
export interface MatrixValues {
  fileType?: string;
  fileSize?: number;
  extractedFiles?: number;
  threatName?: string;
  scannerMatch?: number;
  scannerCount?: number;
  certValidation?: string;
  systemTags?: string[];
  capabilities?: string[];
}

export type MatrixCell =
  | { kind: "unavailable" }
  /** `delta` only on a differing number: how far it is from the main sample. */
  | { kind: "scalar"; text: string; differs: boolean; delta?: string }
  /** `count` is this row's own total, shown above the gained/lost delta. */
  | {
      kind: "set";
      count: number;
      gained: string[];
      lost: string[];
      shared: number;
    };

export const cellDiffers = (cell: MatrixCell): boolean =>
  cell.kind === "scalar"
    ? cell.differs
    : cell.kind === "set" && cell.gained.length + cell.lost.length > 0;

const signed = (difference: number, text: string): string =>
  `${difference < 0 ? "−" : "+"}${text}`;

const countDelta = (
  value: number | undefined,
  reference: number | undefined
): string | undefined =>
  value === undefined || reference === undefined
    ? undefined
    : signed(value - reference, String(Math.abs(value - reference)));

/**
 * Three payloads spell file type three ways: the sample record's type_display
 * is TiCore's ("PE/Exe"), Solr builds its own from file_type, file_subtype and
 * identification_name ("PE/Exe/PyInstaller", sample_search/utils.py), and RCA2
 * sends a third. They agree on the leading format and subtype, so the
 * comparison uses those and the cell still shows the row's full string.
 */
const fileTypeKey = (value?: string): string | undefined =>
  value ? value.toLowerCase().split("/").slice(0, 2).join("/") : undefined;

/** cert_classification ships slugs (samples/cert.py); these are for people. */
const CERT_LABELS: Record<string, string> = {
  no: "No certificate",
  invalid: "Invalid",
  "valid-trusted": "Valid, trusted",
  "valid-untrusted": "Valid, untrusted"
};

const certLabel = (value: string): string => CERT_LABELS[value] ?? value;

/** A zero total means the scanners never ran, not that nothing matched. */
const hasScannerCounts = (values: MatrixValues): boolean =>
  Boolean(values.scannerCount);

type ScalarSpec = {
  kind: "scalar";
  title: string;
  shortTitle: string;
  /** undefined when this side has no data; text() is then not called. */
  key: (values: MatrixValues) => string | undefined;
  text: (values: MatrixValues) => string;
  /** Numbers only; called only when both sides have data and differ. */
  delta?: (values: MatrixValues, reference: MatrixValues) => string | undefined;
};

type SetSpec = {
  kind: "set";
  title: string;
  shortTitle: string;
  entries: (values: MatrixValues) => string[] | undefined;
};

type ColumnSpec = ScalarSpec | SetSpec;

const COLUMN_SPECS: Record<MatrixColumnName, ColumnSpec> = {
  file_type: {
    kind: "scalar",
    title: "Type",
    shortTitle: "Type",
    key: values => fileTypeKey(values.fileType),
    text: values => values.fileType || EMPTY_VALUE
  },
  file_size: {
    kind: "scalar",
    title: "File Size",
    shortTitle: "Size",
    key: values =>
      values.fileSize === undefined ? undefined : String(values.fileSize),
    text: values => String(convertBytesToString(values.fileSize)),
    delta: (values, reference) => {
      const difference = (values.fileSize ?? 0) - (reference.fileSize ?? 0);
      return signed(
        difference,
        String(convertBytesToString(Math.abs(difference)))
      );
    }
  },
  extracted_files: {
    kind: "scalar",
    title: "Extracted Files",
    shortTitle: "Files",
    key: values =>
      values.extractedFiles === undefined
        ? undefined
        : String(values.extractedFiles),
    text: values => String(values.extractedFiles),
    delta: (values, reference) =>
      countDelta(values.extractedFiles, reference.extractedFiles)
  },
  threat_name: {
    kind: "scalar",
    title: "Threat Name",
    shortTitle: "Threat Name",
    // Folded for comparison only - the cell shows the name as it was given.
    key: values => values.threatName?.toLowerCase(),
    // A loaded row with no name is clean, which differs from a named main
    // sample; the dash stays for "no data yet".
    text: values => values.threatName || EMPTY_VALUE
  },
  av_detections: {
    kind: "scalar",
    title: "AV Detections",
    shortTitle: "AV",
    key: values =>
      hasScannerCounts(values)
        ? `${values.scannerMatch ?? 0}/${values.scannerCount}`
        : undefined,
    text: values => `${values.scannerMatch ?? 0} of ${values.scannerCount}`,
    delta: (values, reference) =>
      countDelta(values.scannerMatch ?? 0, reference.scannerMatch ?? 0)
  },
  cert_validation: {
    kind: "scalar",
    title: "Certificate",
    shortTitle: "Certificate",
    key: values => values.certValidation,
    text: values => certLabel(String(values.certValidation))
  },
  system_tags: {
    kind: "set",
    title: "System Tags",
    shortTitle: "Tags",
    entries: values => values.systemTags
  },
  capabilities: {
    kind: "set",
    title: "Capabilities",
    shortTitle: "Caps",
    entries: values => values.capabilities
  }
};

export interface MatrixColumn {
  name: MatrixColumnName;
  title: string;
  shortTitle: string;
}

export const MATRIX_COLUMNS: MatrixColumn[] = (
  Object.keys(COLUMN_SPECS) as MatrixColumnName[]
).map(name => ({
  name,
  title: COLUMN_SPECS[name].title,
  shortTitle: COLUMN_SPECS[name].shortTitle
}));

/**
 * Both grids count the container in their Files column, so the reference adds
 * one: extracted_file_count is the raw model field.
 */
export const matrixValuesFromDetails = (
  details: ExpandedLocalDetails
): MatrixValues => {
  const sample = details.sample;
  return {
    fileType: sample.type_display ?? undefined,
    fileSize: sample.file_size ?? undefined,
    extractedFiles:
      sample.extracted_file_count === undefined ||
      sample.extracted_file_count === null
        ? undefined
        : sample.extracted_file_count + 1,
    threatName: sample.threat_name ?? "",
    certValidation: sample.cert_classification ?? undefined,
    systemTags: sample.tags?.ticore_tags ?? undefined,
    capabilities: sample.details?.data ? enabledCapabilities(sample) : undefined
  };
};

export interface DetectionCounts {
  scanner_match?: number | null;
  scanner_count?: number | null;
}

/** The details payload carries RCA2's AV totals, which disagree with the expanded row. */
export const matrixValuesFromDetections = (
  detections?: DetectionCounts
): MatrixValues => ({
  scannerMatch: detections?.scanner_match ?? undefined,
  scannerCount: detections?.scanner_count ?? undefined
});

export const matrixValuesFromRow = (
  sample: FileSimilaritySample
): MatrixValues => {
  if (sample.loading || sample.is_not_found) {
    return {};
  }

  // Size and count always arrive together, so a seeded zero size means the
  // count is equally untrustworthy. Zero extracted files is itself a real
  // answer, which is why the count is guarded on the size.
  const fileSize = sample.file_size || undefined;

  return {
    // Local and rldata messages carry type_display, the RCA2 message its own
    // sample_type; a row gets whichever arrived.
    fileType: sample.type_display || sample.sample_type || undefined,
    fileSize,
    extractedFiles:
      fileSize === undefined || sample.file_count === undefined
        ? undefined
        : sample.file_count + 1,
    threatName: sample.threat_name ?? "",
    scannerMatch: sample.scanner_match ?? undefined,
    scannerCount: sample.scanner_count ?? undefined,
    certValidation: sample.cert_classification ?? undefined,
    systemTags: sample.system_tags ?? undefined,
    capabilities: sample.capabilities ?? undefined
  };
};

const unavailableCell: MatrixCell = { kind: "unavailable" };

export const matrixCell = (
  column: MatrixColumnName,
  values: MatrixValues,
  reference: MatrixValues | undefined
): MatrixCell => {
  const spec = COLUMN_SPECS[column];

  if (spec.kind === "set") {
    const entries = spec.entries(values);
    const referenceEntries = reference ? spec.entries(reference) : undefined;
    // "+0 / -0" would read as "identical" rather than "not known yet".
    if (entries === undefined || referenceEntries === undefined) {
      return unavailableCell;
    }
    const diff = diffSets(referenceEntries, entries);
    return {
      kind: "set",
      count: entries.length,
      gained: diff.otherChips
        .filter(chip => !chip.shared)
        .map(chip => chip.text),
      lost: diff.mainChips.filter(chip => !chip.shared).map(chip => chip.text),
      shared: diff.shared
    };
  }

  const key = spec.key(values);
  if (key === undefined) {
    return unavailableCell;
  }

  const referenceKey = reference ? spec.key(reference) : undefined;
  const differs = referenceKey !== undefined && referenceKey !== key;

  return {
    kind: "scalar",
    text: spec.text(values),
    differs,
    delta:
      differs && reference && spec.delta
        ? spec.delta(values, reference)
        : undefined
  };
};

/** Unlike matrixCell, a value on one side only differs: both samples are fully loaded. */
export const columnDiffers = (
  column: MatrixColumnName,
  a: MatrixValues,
  b: MatrixValues
): boolean => {
  const spec = COLUMN_SPECS[column];
  if (spec.kind === "set") {
    const diff = diffSets(spec.entries(a), spec.entries(b));
    return diff.onlyMain + diff.onlyOther > 0;
  }
  return spec.key(a) !== spec.key(b);
};

export const referenceCellText = (
  column: MatrixColumnName,
  reference: MatrixValues | undefined
): string => {
  if (!reference) {
    return EMPTY_VALUE;
  }

  const spec = COLUMN_SPECS[column];

  if (spec.kind === "set") {
    const entries = spec.entries(reference);
    if (entries === undefined) {
      return EMPTY_VALUE;
    }
    return String(entries.length);
  }

  return spec.key(reference) === undefined ? EMPTY_VALUE : spec.text(reference);
};
